<script setup lang="ts">
import { v4 as uuid } from "uuid";
import { ref, computed, onMounted } from "vue";
import PhosphorIcon from "@/components/UI/PhosphorIcon.vue";
import { Facade } from "@/drawing";
import { drawingImageRepository } from "@/repositories/drawingImage.repository";
import CanvasSettingsState from "@/stores/CanvasSettingsState";
import { trackTab } from "@/analytics/drawing-analytics";

const props = defineProps<{
  pdfDocument: any;
}>();

const emit = defineEmits<{
  close: [];
}>();

// ── State ────────────────────────────────────────────────────────────────────

const thumbnails = ref<string[]>([]); // data URLs for preview
const selected = ref<Set<number>>(new Set()); // 1-based page indices
const adding = ref(false);

const totalPages = computed(() => props.pdfDocument.numPages);
const selectedCount = computed(() => selected.value.size);
const allSelected = computed(() => selected.value.size === totalPages.value);

// ── Thumbnail generation ─────────────────────────────────────────────────────

const THUMBNAIL_WIDTH = 110;

onMounted(async () => {
  const urls: string[] = [];
  for (let i = 1; i <= totalPages.value; i++) {
    const page = await props.pdfDocument.getPage(i);
    const viewport = page.getViewport({ scale: 1 });
    // Rendu à la densité de l'écran : la miniature reste nette.
    const scale =
      (THUMBNAIL_WIDTH * (window.devicePixelRatio || 1)) / viewport.width;
    const scaledViewport = page.getViewport({ scale });

    const canvas = document.createElement("canvas");
    canvas.width = scaledViewport.width;
    canvas.height = scaledViewport.height;
    const ctx = canvas.getContext("2d")!;
    ctx.fillStyle = "#fff";
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    await page.render({ canvasContext: ctx, viewport: scaledViewport }).promise;
    urls.push(canvas.toDataURL("image/png"));
  }
  thumbnails.value = urls;
});

// ── Selection ────────────────────────────────────────────────────────────────

function togglePage(pageNumber: number) {
  const s = new Set(selected.value);
  if (s.has(pageNumber)) {
    s.delete(pageNumber);
  } else {
    s.add(pageNumber);
  }
  selected.value = s;
}

function toggleAll() {
  if (allSelected.value) {
    selected.value = new Set();
  } else {
    selected.value = new Set(
      Array.from({ length: totalPages.value }, (_, i) => i + 1),
    );
  }
}

// ── Insert selected pages ────────────────────────────────────────────────────

const RENDER_SCALE = 1.5;

async function addSelectedPages() {
  if (!Facade.isInitialized || selected.value.size === 0) return;
  adding.value = true;

  const sortedPages = Array.from(selected.value).sort((a, b) => a - b);

  for (const [index, pageNum] of sortedPages.entries()) {
    const page = await props.pdfDocument.getPage(pageNum);
    const viewport = page.getViewport({ scale: RENDER_SCALE });

    const canvas = document.createElement("canvas");
    canvas.width = viewport.width;
    canvas.height = viewport.height;
    const ctx = canvas.getContext("2d")!;
    ctx.fillStyle = "#fff";
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    await page.render({ canvasContext: ctx, viewport }).promise;

    const blob: Blob = await new Promise((resolve) =>
      canvas.toBlob((b) => resolve(b!), "image/png"),
    );
    const arrayBuffer = await blob.arrayBuffer();
    const data = new Uint8Array(arrayBuffer);
    const uid = uuid().slice(0, 8);
    const filename = `page_${pageNum}_${uid}.png`;

    const rasterPath = Facade.images.add(filename, data, index);
    if (rasterPath) {
      try {
        const file = new File([blob], filename, { type: "image/png" });
        await drawingImageRepository.appendNewImageOnDrawing(
          CanvasSettingsState.drawingId,
          rasterPath,
          data,
          file,
        );
      } catch (err) {
        console.error(
          `[PdfPagePicker] Failed to persist page ${pageNum}:`,
          err,
        );
      }
    }
  }

  // Tracked once per import, not once per page: the interesting signal is that
  // a PDF was brought in, with its size as detail.
  trackTab("images", "pdf_imported", { pageCount: sortedPages.length });

  adding.value = false;
  emit("close");
}
</script>

<template>
  <div class="pdf-picker d-flex flex-column h-100">
    <!-- Header -->
    <div
      class="pdf-picker-header d-flex align-items-center justify-content-between px-2 py-2"
    >
      <span class="text-white-50" style="font-size: 0.75rem">
        {{ $t("canvas:settings.images.pdfPages") }}
        ({{ totalPages }})
      </span>
      <button class="toggle-all-btn" @click="toggleAll">
        {{
          allSelected
            ? $t("canvas:settings.images.deselectAll")
            : $t("canvas:settings.images.selectAll")
        }}
      </button>
    </div>

    <!-- Thumbnail grid -->
    <div class="pdf-picker-grid flex-grow-1 overflow-y-auto">
      <div
        v-for="(thumb, index) in thumbnails"
        :key="index"
        class="pdf-page-cell"
        :class="{ selected: selected.has(index + 1) }"
        @click="togglePage(index + 1)"
      >
        <img :src="thumb" class="pdf-thumb" />
        <div class="pdf-page-overlay">
          <PhosphorIcon
            name="check-circle"
            :size="22"
            weight="fill"
            class="check-icon"
          />
        </div>
        <span class="pdf-page-number">{{ index + 1 }}</span>
      </div>

      <!-- Loading skeletons while thumbnails render -->
      <div
        v-for="i in totalPages - thumbnails.length"
        :key="'sk-' + i"
        class="pdf-page-cell pdf-page-skeleton"
      />
    </div>

    <!-- Sticky footer -->
    <div class="pdf-picker-footer px-2 py-2">
      <button
        class="add-btn w-100"
        :disabled="selectedCount === 0 || adding"
        @click="addSelectedPages"
      >
        <span v-if="adding">
          {{ $t("canvas:settings.images.adding") }}
        </span>
        <span v-else>
          {{ $t("canvas:settings.images.addPages", { n: selectedCount }) }}
        </span>
      </button>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.pdf-picker {
  min-height: 0;
}

.pdf-picker-header {
  border-bottom: 1px solid var(--canvas-border);
  flex-shrink: 0;
}

.toggle-all-btn {
  background: transparent;
  border: 1px solid var(--canvas-border);
  border-radius: 6px;
  color: rgba(255, 255, 255, 0.6);
  font-size: 0.7rem;
  padding: 3px 8px;
  cursor: pointer;
  &:hover {
    color: white;
    background: rgba(255, 255, 255, 0.08);
  }
}

// Autant de colonnes que la largeur en permet, à la taille des miniatures.
.pdf-picker-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(110px, 1fr));
  align-content: start;
  gap: 8px;
  padding: 8px;
}

.pdf-page-cell {
  position: relative;
  border-radius: 6px;
  overflow: hidden;
  cursor: pointer;
  border: 2px solid transparent;
  background: rgba(255, 255, 255, 0.04);
  transition: border-color 0.15s;

  &:hover {
    border-color: rgba(255, 255, 255, 0.25);
  }

  &.selected {
    border-color: var(--canvas-btn-active-bg);
    .pdf-page-overlay {
      opacity: 1;
    }
  }
}

.pdf-thumb {
  width: 100%;
  display: block;
}

.pdf-page-overlay {
  position: absolute;
  inset: 0;
  background: rgba(0, 0, 0, 0.35);
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0;
  transition: opacity 0.15s;

  .check-icon {
    font-size: 1.4rem;
    color: var(--canvas-btn-active-bg);
  }
}

.pdf-page-number {
  position: absolute;
  bottom: 4px;
  right: 6px;
  font-size: 0.65rem;
  color: rgba(255, 255, 255, 0.7);
  background: rgba(0, 0, 0, 0.5);
  border-radius: 3px;
  padding: 1px 4px;
  line-height: 1.4;
}

.pdf-page-skeleton {
  aspect-ratio: 0.707; // A4 ratio
  background: rgba(255, 255, 255, 0.05);
  animation: skeleton-pulse 1.2s ease-in-out infinite;
}

@keyframes skeleton-pulse {
  0%,
  100% {
    opacity: 0.5;
  }
  50% {
    opacity: 1;
  }
}

.pdf-picker-footer {
  border-top: 1px solid var(--canvas-border);
  flex-shrink: 0;
}

.add-btn {
  padding: 10px;
  background-color: var(--canvas-btn-active-bg);
  border: none;
  border-radius: 8px;
  color: white;
  font-size: 0.85rem;
  font-weight: 600;
  cursor: pointer;
  transition: opacity 0.15s;

  &:hover:not(:disabled) {
    opacity: 0.85;
  }

  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }
}
</style>
